import { TFunction } from '_metronic/i18n/Metronici18n'
import { IOption } from 'app/components'
import { TIPOS_FORNECEDOR, TipoFornecedor } from '../../fornecedores/core/_models'
import {
  CLASSES_RISCO,
  ClasseRiscoDispositivo,
  MOTIVOS_BAIXA,
  MOTIVOS_FIM_ALOCACAO,
  MaletaAtual,
  MotivoBaixaDispositivo,
  MotivoFimAlocacao,
  REGIMES_DISPOSITIVO,
  RegimeDispositivo,
  SITUACOES_DISPOSITIVO,
  SituacaoDispositivo,
  TIPOS_DISPOSITIVO,
  TipoDispositivoGestao,
} from './_models'

/*
 * Rótulos pt-BR dos enums da gestão de dispositivos e fornecedores, lidos do `pt-br.json`.
 * Os tipos Hilab ficam aqui, e não no `DispositivoType` global, que é usado por ícones do
 * atendimento com `Record<DispositivoType, …>`.
 */

export const rotuloTipo = (t: TFunction, tipo?: TipoDispositivoGestao | null) =>
  tipo ? t(`dispositivos.rotulos.tipo.${tipo}`) : ''

export const rotuloSituacao = (t: TFunction, situacao?: SituacaoDispositivo | null) =>
  situacao ? t(`dispositivos.rotulos.situacao.${situacao}`) : ''

export const rotuloRegime = (t: TFunction, regime?: RegimeDispositivo | null) =>
  regime ? t(`dispositivos.rotulos.regime.${regime}`) : ''

export const rotuloClasseRisco = (t: TFunction, classe?: ClasseRiscoDispositivo | null) =>
  classe ? t(`dispositivos.rotulos.classe_risco.${classe}`) : ''

export const rotuloMotivoBaixa = (t: TFunction, motivo?: MotivoBaixaDispositivo | null) =>
  motivo ? t(`dispositivos.rotulos.motivo_baixa.${motivo}`) : ''

export const rotuloMotivoFim = (t: TFunction, motivo?: MotivoFimAlocacao | null) =>
  motivo ? t(`dispositivos.rotulos.motivo_fim.${motivo}`) : ''

export const rotuloTipoFornecedor = (t: TFunction, tipo?: TipoFornecedor | null) =>
  tipo ? t(`dispositivos.rotulos.tipo_fornecedor.${tipo}`) : ''

/** "Maleta Padrão", "Telekit 1021" ou `''` sem maleta. A Padrão é reconhecida só por `padrao`. */
export const rotuloMaleta = (
  t: TFunction,
  maleta?: Pick<MaletaAtual, 'identificador' | 'padrao'> | null
) => {
  if (!maleta) return ''
  if (maleta.padrao) return t('dispositivos.rotulos.maleta_padrao')
  return t('dispositivos.rotulos.maleta_telekit', { identificador: maleta.identificador ?? '' })
}

/** "Oxímetro série 123" (`pagina.dispositivos.nome_item`). */
export const rotuloDispositivo = (
  t: TFunction,
  d: { tipo?: TipoDispositivoGestao | null; serie?: string | null }
) => t('pagina.dispositivos.nome_item', { tipo: rotuloTipo(t, d.tipo), serie: d.serie })

/** Junta as partes preenchidas com " · ", para a linha secundária das células. */
export const linhaApoio = (...partes: Array<string | null | undefined>) =>
  partes.filter(Boolean).join(' · ')

export const opcao = <T extends string>(
  valor: T | null | undefined,
  rotulo: (valor: T) => string
): IOption<T> | undefined => (valor ? { value: valor, label: rotulo(valor) } : undefined)

/*
 * As listas são montadas a cada chamada: quem passa para o `CustomSelect` memoriza com
 * `useMemo(..., [t])`, já que ele ressincroniza `options` a cada referência nova.
 */
const opcoes = <T extends string>(valores: T[], rotulo: (valor: T) => string): IOption<T>[] =>
  valores.map((valor) => ({ value: valor, label: rotulo(valor) }))

export const opcoesTipo = (t: TFunction) => opcoes(TIPOS_DISPOSITIVO, (v) => rotuloTipo(t, v))

export const opcoesSituacao = (t: TFunction, semEmUso = false) =>
  opcoes(
    SITUACOES_DISPOSITIVO.filter((s) => !semEmUso || s !== 'EM_USO'),
    (v) => rotuloSituacao(t, v)
  )

export const opcoesRegime = (t: TFunction) => opcoes(REGIMES_DISPOSITIVO, (v) => rotuloRegime(t, v))

export const opcoesClasseRisco = (t: TFunction) =>
  opcoes(CLASSES_RISCO, (v) => rotuloClasseRisco(t, v))

export const opcoesMotivoBaixa = (t: TFunction) =>
  opcoes(MOTIVOS_BAIXA, (v) => rotuloMotivoBaixa(t, v))

export const opcoesMotivoFim = (t: TFunction) =>
  opcoes(MOTIVOS_FIM_ALOCACAO, (v) => rotuloMotivoFim(t, v))

export const opcoesTipoFornecedor = (t: TFunction) =>
  opcoes(TIPOS_FORNECEDOR, (v) => rotuloTipoFornecedor(t, v))
